/**
 * 🛠 TEKNİK SEO AJANI — Semrush Site Audit mantığında site denetimi (DataForSEO OnPage):
 * Site Health skoru + Hata/Uyarı/Bilgi sayaçları + domain kontrolleri + tematik issue
 * listesi (her bulguda "neden önemli + nasıl çözülür" + etkilenen sayfalar) + en kötü
 * sayfalar tablosu + duplicate gruplar + redirect zincirleri + ana sayfa Core Web Vitals.
 */
import { confirmCost } from "@/components/cost-confirm";
import { useState, useRef, useEffect } from "react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, Loader2, Wrench, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, Cell, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c" };
const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));

type Issue = {
  key: string; severity: "error" | "warning" | "notice";
  category?: string;
  label: string; why: string; fix: string; count: number;
  newCount?: number;
  pages: { url: string; extra?: string }[];
};
type AuditData = {
  target: string; taskId: string;
  crawledPages: number; maxPages: number;
  healthScore: number;
  domainChecks: { key: string; label: string; ok: boolean }[];
  issues: Issue[];
  totals: { errors: number; warnings: number; notices: number };
  worstPages: { url: string; score: number; statusCode: number; loadTimeMs: number; sizeKb: number; title: string; internalLinks: number; externalLinks: number; failing: string[] }[];
  scoreBuckets: { bucket: string; count: number }[];
  duplicateTitles: { value: string; pages: string[] }[];
  duplicateDescriptions: { value: string; pages: string[] }[];
  redirectChains: string[][];
  nonIndexable: { url: string; reason: string }[];
  cwv?: { performance: number; lcpMs: number; cls: number; fcpMs: number; tbtMs: number } | null;
  jsRendering?: boolean;
  pageBreakdown?: { healthy: number; broken: number; haveIssues: number; redirects: number; blocked: number };
  statusDist?: { c2xx: number; c3xx: number; c4xx: number; c5xx: number; other: number };
  depthDist?: { d1: number; d2: number; d3: number; d4plus: number };
  inlinkBuckets?: { one: number; b2_5: number; b6_15: number; b16_50: number; b51plus: number };
  canonicalDist?: { self: number; other: number; none: number };
  markup?: { og: number; twitter: number; total: number };
  sitemapCompare?: { sitemapUrls: number; crawledInSitemap: number; crawledNotInSitemap: number; sitemapNotCrawledSample: string[] };
  aiSearch?: { score: number; llmsTxt: boolean; robotsTxtFound: boolean; bots: { bot: string; allowed: boolean }[]; notes: string[] };
  resources?: { total: number; brokenByType: { type: string; count: number }[]; brokenSamples: { url: string; type: string; status: number }[]; oversizedImages: { url: string; sizeKb: number }[]; uncompressed: number; mixedContent: { url: string; type: string }[] };
  securityHeaders?: { header: string; ok: boolean; value: string | null; why: string }[];
  internalBrokenLinks?: { sourceUrl: string; targetUrl: string; anchor: string }[];
  duplicateContent?: { flaggedPages: number; pairs: { url1: string; url2: string; similarity: number }[] };
  microdata?: { pagesWithSchema: number; pagesWithErrors: number; errorSamples: { url: string; messages: string[] }[] };
  hreflang?: { sampledPages: number; pagesWithHreflang: number; invalidCodes: { url: string; code: string }[]; missingSelfRef: string[]; missingReturn: { from: string; to: string }[]; conflicts: { url: string; lang: string }[] };
  waterfall?: { url: string; ttfb: number; connection: number; download: number; duration: number }[];
  gscOrphans?: { count: number; rows: { url: string; impressions: number; clicks: number }[] };
  diff?: {
    prevRunAt: string; healthDelta: number; errorsDelta: number; warningsDelta: number; noticesDelta: number;
    newIssues: { key: string; label: string; count: number }[];
    fixedIssues: { key: string; label: string; prevCount: number }[];
  } | null;
  aiComment?: string; aiCommentError?: string; cost: number;
};
type Resp = {
  configured: boolean; data: AuditData | null; runAt: string | null;
  history?: { runAt: string; health: number; errors: number; warnings: number }[];
  running: boolean; progress: { crawled: number; max: number; stage: string } | null; runError: string | null;
};
const delta = (n?: number) => (n === undefined || n === 0 ? null : <span className="text-[10px] font-semibold ml-1" style={{ color: n > 0 ? C.red : C.green }}>{n > 0 ? `+${n}` : n}</span>);
const deltaGood = (n?: number) => (n === undefined || n === 0 ? null : <span className="text-[10px] font-semibold ml-1" style={{ color: n > 0 ? C.green : C.red }}>{n > 0 ? `+${n}` : n}</span>);

const sevMeta = {
  error: { l: "Hata", c: C.red, bg: "rgba(239,68,68,0.1)" },
  warning: { l: "Uyarı", c: C.amber, bg: "rgba(245,158,11,0.12)" },
  notice: { l: "Bilgi", c: C.cyan, bg: "rgba(6,182,212,0.1)" },
} as const;
const healthColor = (s: number) => (s >= 85 ? C.green : s >= 70 ? C.amber : C.red);
const pathOf = (u: string) => u.replace(/^https?:\/\/[^/]+/, "") || "/";

export default function TechAudit() {
  const { data: domains } = useListDomains();
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [maxPages, setMaxPages] = useState("150");
  const [enableJs, setEnableJs] = useState(false);
  const [openIssue, setOpenIssue] = useState<string | null>(null);
  const [sevFilter, setSevFilter] = useState<"all" | "error" | "warning" | "notice">("all");
  const [catFilter, setCatFilter] = useState<string>("all");
  const [issueSearch, setIssueSearch] = useState("");

  const { data: resp } = useQuery({
    queryKey: ["tech-audit", activeDomainId],
    enabled: !!activeDomainId,
    refetchInterval: (q) => ((q.state.data as Resp | undefined)?.running ? 5000 : false),
    queryFn: async (): Promise<Resp> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/tech-audit`); return r.ok ? await r.json() : { configured: false, data: null, runAt: null, running: false, progress: null, runError: null }; }
      catch { return { configured: false, data: null, runAt: null, running: false, progress: null, runError: null }; }
    },
  });
  const d = resp?.data ?? null;
  const running = !!resp?.running;

  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) {
      if (resp?.runError) toast({ title: "❌ Teknik tarama başarısız", description: resp.runError, variant: "destructive" });
      else toast({ title: "✅ Teknik denetim tamamlandı", description: "Rapor hazır — sorunlar ve çözümleri listelendi." });
      queryClient.invalidateQueries({ queryKey: ["tech-audit", activeDomainId] });
    }
    wasRunning.current = running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const run = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/tech-audit`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ maxPages: Number(maxPages), enableJs }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Başlatılamadı");
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "🕷 Tarama başlatıldı", description: "Sayfa sayısına göre 2-10 dk sürer — ilerleme burada görünür." });
      queryClient.invalidateQueries({ queryKey: ["tech-audit", activeDomainId] });
    },
    onError: (e) => toast({ title: "Hata", description: (e as Error).message, variant: "destructive" }),
  });

  const categories = [...new Set((d?.issues ?? []).map((i) => i.category ?? "Diğer"))];
  const shownIssues = (d?.issues ?? [])
    .filter((i) => sevFilter === "all" || i.severity === sevFilter)
    .filter((i) => catFilter === "all" || (i.category ?? "Diğer") === catFilter)
    .filter((i) => !issueSearch || i.label.toLowerCase().includes(issueSearch.toLowerCase()));

  return (
    <div className="px-4 sm:px-6 py-6 w-full">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm" style={{ color: C.purple }}><Wrench className="w-4 h-4" /> Teknik SEO</div>
          <h1 className="text-2xl font-bold text-foreground mt-0.5">Teknik SEO Ajanı</h1>
          <p className="text-sm text-muted-foreground">Site sağlığı denetimi: hatalar, nedenleri ve çözümleri — Semrush Site Audit mantığında.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] px-2.5 py-1 rounded-lg font-medium" style={running ? { background: `${C.cyan}18`, color: "#b12220" } : { background: "rgba(34,197,94,0.1)", color: "#039855" }}>
            {running ? "🕷 Taranıyor" : "● Hazır"}
          </span>
        </div>
      </div>

      {/* Başlat paneli + ilerleme */}
      <Card className="mb-4"><CardContent className="p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <div className="text-xs font-semibold mb-1.5">🕷 Tarama derinliği</div>
            <Select value={maxPages} onValueChange={setMaxPages}>
              <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50 sayfa (~2 dk)</SelectItem>
                <SelectItem value="150">150 sayfa (~4 dk)</SelectItem>
                <SelectItem value="300">300 sayfa (~6 dk)</SelectItem>
                <SelectItem value="500">500 sayfa (~10 dk)</SelectItem>
                <SelectItem value="2000">2.000 sayfa (~20 dk)</SelectItem>
                <SelectItem value="10000">Tüm site — 10.000'e kadar (~25 dk)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none pb-3" title="JS ile render edilen içerik de taranır (sayfa maliyeti ~2x, süre uzar)">
            <input type="checkbox" checked={enableJs} onChange={(e) => setEnableJs(e.target.checked)} className="accent-purple-600" />
            JS render
          </label>
          <button disabled={!activeDomainId || running || run.isPending} onClick={async () => { if (await confirmCost("tech-audit")) run.mutate(); }}
            className="px-5 py-3 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
            style={{ background: "linear-gradient(135deg, #d42b29, #e83e3c)" }}>
            {running || run.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Taranıyor…</> : <>🛠 {d ? "Yeniden Tara" : "Denetimi Başlat"}</>}
          </button>
          {running && resp?.progress && (
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                <span>{resp.progress.stage}</span>
                <span className="tabular-nums">{resp.progress.crawled}/{resp.progress.max} sayfa</span>
              </div>
              <div className="h-2 rounded-full" style={{ background: "rgba(0,0,0,0.06)" }}>
                <div className="h-2 rounded-full transition-all" style={{ width: `${Math.max(4, (resp.progress.crawled / Math.max(1, resp.progress.max)) * 100)}%`, background: `linear-gradient(90deg, ${C.purple}, ${C.cyan})` }} />
              </div>
            </div>
          )}
        </div>
        {d && resp?.runAt && !running && <div className="text-[11px] text-muted-foreground mt-2">Son denetim: {new Date(resp.runAt).toLocaleString("tr-TR")} · {d.crawledPages} sayfa · DataForSEO ${d.cost.toFixed(2)}</div>}
        {resp?.runError && <div className="text-xs text-red-500 mt-2">❌ {resp.runError}</div>}
      </CardContent></Card>

      {!d && !running && (
        <Card><CardContent className="p-12 text-center text-sm text-muted-foreground">
          <Wrench className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Henüz teknik denetim yok — derinliği seçip "Denetimi Başlat"a bas. 60+ teknik kontrol, çözüm önerileriyle gelir.
        </CardContent></Card>
      )}

      {d && (<>
        {/* ── KIYAS ŞERİDİ (önceki taramaya göre — Semrush Compare Crawls) ── */}
        {d.diff && (
          <Card className="mb-4" style={{ borderColor: `${C.cyan}44` }}><CardContent className="p-3.5">
            <div className="flex items-center gap-4 flex-wrap text-xs">
              <span className="font-semibold">📊 Önceki taramaya göre ({new Date(d.diff.prevRunAt).toLocaleDateString("tr-TR")}):</span>
              <span>Sağlık {d.healthScore}{deltaGood(d.diff.healthDelta)}</span>
              <span>Hata {d.totals.errors}{delta(d.diff.errorsDelta)}</span>
              <span>Uyarı {d.totals.warnings}{delta(d.diff.warningsDelta)}</span>
              <span>Bilgi {d.totals.notices}{delta(d.diff.noticesDelta)}</span>
              {d.diff.fixedIssues.length > 0 && <span className="text-success-600 dark:text-success-500">✅ {d.diff.fixedIssues.length} tür çözüldü: {d.diff.fixedIssues.slice(0, 3).map((f) => f.label).join(", ")}{d.diff.fixedIssues.length > 3 ? "…" : ""}</span>}
              {d.diff.newIssues.length > 0 && <span style={{ color: C.red }}>🆕 {d.diff.newIssues.length} yeni tür: {d.diff.newIssues.slice(0, 3).map((f) => f.label).join(", ")}{d.diff.newIssues.length > 3 ? "…" : ""}</span>}
            </div>
          </CardContent></Card>
        )}

        {/* ── ÜST: Site Health + sayaçlar + skor dağılımı + CWV ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
          {/* Site Health radial */}
          <Card><CardContent className="p-4">
            <div className="text-[11px] font-semibold text-muted-foreground mb-1">Site Sağlığı</div>
            <div className="h-[160px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ v: d.healthScore }]} startAngle={220} endAngle={-40}>
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar dataKey="v" cornerRadius={8} fill={healthColor(d.healthScore)} background={{ fill: "rgba(0,0,0,0.05)" }} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-4xl font-bold tabular-nums" style={{ color: healthColor(d.healthScore) }}>{d.healthScore}</span>
                <span className="text-[10px] text-muted-foreground">/ 100</span>
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground text-center">{d.crawledPages} sayfa tarandı</div>
          </CardContent></Card>

          {/* Sayaçlar */}
          <Card><CardContent className="p-4">
            <div className="text-[11px] font-semibold text-muted-foreground mb-3">Bulgular</div>
            {(["error", "warning", "notice"] as const).map((s) => {
              const m = sevMeta[s];
              const total = s === "error" ? d.totals.errors : s === "warning" ? d.totals.warnings : d.totals.notices;
              const kinds = d.issues.filter((i) => i.severity === s).length;
              return (
                <button key={s} title={`${m.l} bulgularını detaylı gör`}
                  onClick={() => {
                    setSevFilter(sevFilter === s ? "all" : s);
                    // Sayaç tıklanınca kullanıcı detayı görmek istiyor — listeye in
                    window.setTimeout(() => document.getElementById("sorun-listesi")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
                  }} className="w-full flex items-center justify-between py-2 px-2 rounded-lg transition mb-1 hover:bg-black/[0.03] dark:hover:bg-white/[0.05]"
                  style={{ background: sevFilter === s ? m.bg : undefined }}>
                  <span className="flex items-center gap-2 text-sm"><span className="w-2.5 h-2.5 rounded-full" style={{ background: m.c }} />{m.l}</span>
                  <span className="text-lg font-bold tabular-nums" style={{ color: m.c }}>{fmt(total)}<span className="text-[10px] font-normal text-muted-foreground ml-1">({kinds} tür)</span></span>
                </button>
              );
            })}
          </CardContent></Card>

          {/* Skor dağılımı */}
          <Card><CardContent className="p-4">
            <div className="text-[11px] font-semibold text-muted-foreground mb-1">Sayfa Skor Dağılımı</div>
            <div className="h-[170px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.scoreBuckets} margin={{ top: 10, right: 4, left: -24, bottom: 0 }}>
                  <XAxis dataKey="bucket" tick={{ fontSize: 9, fill: "#a3a3a0" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#a3a3a0" }} allowDecimals={false} />
                  <RTooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} formatter={(v: number) => [v, "sayfa"]} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {d.scoreBuckets.map((b, i) => <Cell key={i} fill={[C.red, C.amber, "#facc15", "#a3e635", C.green][i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent></Card>

          {/* Domain kontrolleri + CWV */}
          <Card><CardContent className="p-4">
            <div className="text-[11px] font-semibold text-muted-foreground mb-2">Domain Kontrolleri</div>
            <div className="space-y-1">
              {d.domainChecks.map((c) => (
                <div key={c.key} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{c.label}</span>
                  <span style={{ color: c.ok ? C.green : C.red }}>{c.ok ? "✓" : "✗"}</span>
                </div>
              ))}
            </div>
            {d.cwv && (
              <div className="mt-3 pt-2 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                <div className="text-[10px] font-semibold text-muted-foreground mb-1.5">ANA SAYFA CWV (mobil)</div>
                <div className="grid grid-cols-2 gap-1 text-[11px]">
                  <span>Performans: <b style={{ color: d.cwv.performance >= 90 ? C.green : d.cwv.performance >= 50 ? C.amber : C.red }}>{d.cwv.performance}</b></span>
                  <span>LCP: <b style={{ color: d.cwv.lcpMs <= 2500 ? C.green : d.cwv.lcpMs <= 4000 ? C.amber : C.red }}>{(d.cwv.lcpMs / 1000).toFixed(1)}s</b></span>
                  <span>CLS: <b style={{ color: d.cwv.cls <= 0.1 ? C.green : d.cwv.cls <= 0.25 ? C.amber : C.red }}>{d.cwv.cls}</b></span>
                  <span>TBT: <b style={{ color: d.cwv.tbtMs <= 200 ? C.green : d.cwv.tbtMs <= 600 ? C.amber : C.red }}>{d.cwv.tbtMs}ms</b></span>
                </div>
              </div>
            )}
          </CardContent></Card>
        </div>

        {/* ── SATIR 2: Crawled Pages dağılımı + AI Search Health + Sitemap kıyası ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
          {d.pageBreakdown && (() => {
            const pb = d.pageBreakdown!;
            const total = pb.healthy + pb.broken + pb.haveIssues + pb.redirects + pb.blocked || 1;
            const segs = [
              { l: "Sağlıklı", v: pb.healthy, c: C.green }, { l: "Kırık", v: pb.broken, c: C.red },
              { l: "Sorunlu", v: pb.haveIssues, c: C.amber }, { l: "Yönlendirme", v: pb.redirects, c: C.purple },
              { l: "Engelli", v: pb.blocked, c: "#a3a3a0" },
            ];
            return (
              <Card><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-2">Taranan Sayfalar <span className="font-normal">· {d.crawledPages}</span>{d.jsRendering && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${C.purple}15`, color: "#d42b29" }}>JS render</span>}</div>
                <div className="flex h-3 rounded-full overflow-hidden mb-3" style={{ background: "rgba(0,0,0,0.05)" }}>
                  {segs.map((s) => s.v > 0 && <div key={s.l} style={{ width: `${(s.v / total) * 100}%`, background: s.c }} title={`${s.l}: ${s.v}`} />)}
                </div>
                <div className="space-y-1">
                  {segs.map((s) => (
                    <div key={s.l} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: s.c }} />{s.l}</span>
                      <b className="tabular-nums">{s.v}</b>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            );
          })()}

          {d.aiSearch && (() => {
            const a = d.aiSearch!;
            const col = a.score >= 80 ? C.green : a.score >= 50 ? C.amber : C.red;
            return (
              <Card style={{ borderColor: `${C.purple}44` }}><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-1">AI Search Health <span className="text-[9px] px-1.5 py-0.5 rounded ml-1" style={{ background: `${C.amber}20`, color: "#b54708" }}>GEO</span></div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl font-bold tabular-nums" style={{ color: col }}>%{a.score}</span>
                  <div className="text-[10px] text-muted-foreground">
                    {/* llms.txt zorunlu bir standart değil — yokluğu HATA gibi (kırmızı ✗) gösterilmez. */}
                    <div title="llms.txt zorunlu değildir; Google ve Bing kullanmaz. Bazı AI istemcileri site rehberi olarak okuyabilir.">
                      llms.txt: <b style={{ color: a.llmsTxt ? C.green : "#a3a3a0" }}>{a.llmsTxt ? "var ✓" : "yok (isteğe bağlı)"}</b>
                    </div>
                    <div>robots.txt: <b style={{ color: a.robotsTxtFound ? C.green : C.red }}>{a.robotsTxtFound ? "okundu ✓" : "yok ✗"}</b></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] mb-2">
                  {a.bots.map((b) => (
                    <div key={b.bot} className="flex items-center justify-between">
                      <span className="text-muted-foreground truncate">{b.bot}</span>
                      <span style={{ color: b.allowed ? C.green : C.red }}>{b.allowed ? "izinli" : "ENGELLİ"}</span>
                    </div>
                  ))}
                </div>
                {a.notes.map((n, i) => <div key={i} className="text-[10px] text-warning-700 dark:text-warning-400">⚠ {n}</div>)}
              </CardContent></Card>
            );
          })()}

          {d.sitemapCompare && (() => {
            const sc = d.sitemapCompare!;
            return (
              <Card><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-2">Sitemap ↔ Tarama Kıyası</div>
                <div className="text-2xl font-bold tabular-nums mb-1">{sc.sitemapUrls} <span className="text-xs font-normal text-muted-foreground">URL sitemap'te</span></div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Taranan + sitemap'te olan</span><b style={{ color: C.green }}>{sc.crawledInSitemap}</b></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Taranan ama sitemap'te YOK</span><b style={{ color: C.amber }}>{sc.crawledNotInSitemap}</b></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Sitemap'te var, taramada görünmedi</span><b style={{ color: sc.sitemapNotCrawledSample.length ? C.red : C.green }}>{sc.sitemapNotCrawledSample.length}{sc.sitemapNotCrawledSample.length >= 20 ? "+" : ""}</b></div>
                </div>
                {sc.sitemapNotCrawledSample.length > 0 && (
                  <div className="mt-2 max-h-[90px] overflow-y-auto text-[10px] text-muted-foreground space-y-0.5">
                    {sc.sitemapNotCrawledSample.slice(0, 8).map((u) => <div key={u} className="truncate">{pathOf(u)}</div>)}
                  </div>
                )}
              </CardContent></Card>
            );
          })()}
        </div>

        {/* ── v3: SCREAMING FROG DERİNLİĞİ — kaynak denetimi + güvenlik + kırık iç linkler ── */}
        {/* ── Faz C: hreflang + hız şelalesi + GSC hayalet sayfalar ── */}
        {(d.hreflang || d.waterfall || d.gscOrphans) && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
          {d.hreflang && (() => {
            const h = d.hreflang!;
            const issueCount = h.invalidCodes.length + h.missingSelfRef.length + h.missingReturn.length + h.conflicts.length;
            return (
              <Card style={issueCount > 0 ? { borderColor: `${C.amber}44` } : undefined}><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-2">🌍 Hreflang Denetimi <span className="text-[9px] font-normal">({h.sampledPages} sayfa örneklendi)</span></div>
                {h.pagesWithHreflang === 0 ? (
                  <div className="text-xs text-muted-foreground">Örneklemde hreflang etiketi bulunamadı — site tek dilliyse sorun değil; çok dilliyse <b className="text-warning-700 dark:text-warning-400">kritik eksik</b>.</div>
                ) : (
                  <>
                    <div className="text-2xl font-bold tabular-nums mb-1" style={{ color: issueCount ? C.amber : C.green }}>{issueCount} <span className="text-xs font-normal text-muted-foreground">sorun · {h.pagesWithHreflang} sayfada hreflang var</span></div>
                    <div className="max-h-[140px] overflow-y-auto space-y-0.5 text-[10px]">
                      {h.invalidCodes.map((x, i) => <div key={`i${i}`} className="truncate" title={x.url}><b style={{ color: C.red }}>geçersiz kod "{x.code}"</b> · {x.url.replace(/^https?:\/\/[^/]+/, "")}</div>)}
                      {h.missingSelfRef.map((u, i) => <div key={`s${i}`} className="truncate" title={u}><b style={{ color: C.amber }}>self-referans yok</b> · {u.replace(/^https?:\/\/[^/]+/, "")}</div>)}
                      {h.missingReturn.map((x, i) => <div key={`r${i}`} className="truncate" title={`${x.from} → ${x.to}`}><b style={{ color: C.amber }}>dönüş linki yok</b> · {x.from.replace(/^https?:\/\/[^/]+/, "")} → {x.to.replace(/^https?:\/\/[^/]+/, "")}</div>)}
                      {h.conflicts.map((x, i) => <div key={`c${i}`} className="truncate" title={x.url}><b style={{ color: C.red }}>çakışan hedef ({x.lang})</b> · {x.url.replace(/^https?:\/\/[^/]+/, "")}</div>)}
                      {issueCount === 0 && <div style={{ color: C.green }}>✓ Örneklemde hreflang tutarlı.</div>}
                    </div>
                  </>
                )}
              </CardContent></Card>
            );
          })()}

          {d.waterfall && d.waterfall.length > 0 && (
            <Card><CardContent className="p-4">
              <div className="text-[11px] font-semibold text-muted-foreground mb-2">⏱ Hız Şelalesi <span className="text-[9px] font-normal">(ana sayfa + en yavaş sayfa)</span></div>
              <div className="space-y-3">
                {d.waterfall.map((w) => {
                  const total = Math.max(w.duration, w.ttfb + w.connection + w.download, 1);
                  const seg = (v: number, c: string, l: string) => v > 0 ? <div title={`${l}: ${v}ms`} style={{ width: `${Math.max(2, (v / total) * 100)}%`, background: c }} className="h-3" /> : null;
                  return (
                    <div key={w.url} className="text-[10px]">
                      <div className="truncate text-muted-foreground mb-1" title={w.url}>{w.url.replace(/^https?:\/\/[^/]+/, "") || "/"} · <b>{w.duration}ms</b></div>
                      <div className="flex rounded overflow-hidden" style={{ background: "rgba(0,0,0,0.05)" }}>
                        {seg(w.connection, "#e83e3c", "Bağlantı")}
                        {seg(w.ttfb, "#f79009", "TTFB (sunucu bekleme)")}
                        {seg(w.download, "#12b76a", "İndirme")}
                      </div>
                      <div className="flex gap-3 mt-0.5 text-muted-foreground">
                        <span><i className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: "#e83e3c" }} />bağlantı {w.connection}ms</span>
                        <span><i className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: "#f79009" }} />TTFB {w.ttfb}ms</span>
                        <span><i className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: "#12b76a" }} />indirme {w.download}ms</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent></Card>
          )}

          {d.gscOrphans && (
            <Card style={d.gscOrphans.count > 0 ? { borderColor: `${C.amber}44` } : undefined}><CardContent className="p-4">
              <div className="text-[11px] font-semibold text-muted-foreground mb-2">👻 GSC Hayalet Sayfalar <span className="text-[9px] font-normal">(gösterim alıyor, taramada yok)</span></div>
              <div className="text-2xl font-bold tabular-nums mb-1" style={{ color: d.gscOrphans.count ? C.amber : C.green }}>{d.gscOrphans.count} <span className="text-xs font-normal text-muted-foreground">sayfa</span></div>
              {d.gscOrphans.rows.length > 0 ? (
                <div className="max-h-[140px] overflow-y-auto space-y-0.5 text-[10px]">
                  {d.gscOrphans.rows.map((r2) => (
                    <div key={r2.url} className="flex justify-between gap-2">
                      <span className="truncate text-muted-foreground" title={r2.url}>{r2.url.replace(/^https?:\/\/[^/]+/, "")}</span>
                      <b className="tabular-nums shrink-0">{r2.impressions} göst.</b>
                    </div>
                  ))}
                </div>
              ) : <div className="text-[10px]" style={{ color: C.green }}>✓ GSC'deki tüm gösterimli sayfalar taramada bulundu.</div>}
              <div className="text-[9px] text-muted-foreground mt-1.5">Sebep: iç link almıyor (orphan), tarama derinliği dışında veya artık silinmiş olabilir.</div>
            </CardContent></Card>
          )}
        </div>
        )}

        {(d.resources || d.securityHeaders || d.duplicateContent || d.microdata || (d.internalBrokenLinks?.length ?? 0) > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
          {d.resources && (() => {
            const rs = d.resources!;
            const brokenTotal = rs.brokenByType.reduce((s, b) => s + b.count, 0);
            return (
              <Card style={brokenTotal > 0 || rs.mixedContent.length > 0 ? { borderColor: `${C.red}44` } : undefined}><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-2">🧱 Kaynak Denetimi <span className="text-[9px] text-muted-foreground font-normal">({rs.total} kaynak: görsel/JS/CSS)</span></div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Kırık kaynak (4xx/5xx)</span><b style={{ color: brokenTotal ? C.red : C.green }}>{brokenTotal}{rs.brokenByType.length ? ` (${rs.brokenByType.map((b) => `${b.count} ${b.type}`).join(", ")})` : ""}</b></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Şişkin görsel (&gt;200KB)</span><b style={{ color: rs.oversizedImages.length ? C.amber : C.green }}>{rs.oversizedImages.length}</b></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Sıkıştırmasız JS/CSS</span><b style={{ color: rs.uncompressed ? C.amber : C.green }}>{rs.uncompressed}</b></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Mixed content (HTTP kaynak)</span><b style={{ color: rs.mixedContent.length ? C.red : C.green }}>{rs.mixedContent.length}</b></div>
                </div>
                {(rs.brokenSamples.length > 0 || rs.oversizedImages.length > 0) && (
                  <div className="mt-2 max-h-[110px] overflow-y-auto text-[10px] text-muted-foreground space-y-0.5">
                    {rs.brokenSamples.slice(0, 6).map((b) => <div key={b.url} className="truncate" title={b.url}><span style={{ color: C.red }}>{b.status}</span> {b.type} · {b.url.replace(/^https?:\/\/[^/]+/, "")}</div>)}
                    {rs.oversizedImages.slice(0, 5).map((o) => <div key={o.url} className="truncate" title={o.url}><span style={{ color: C.amber }}>{o.sizeKb}KB</span> {o.url.replace(/^https?:\/\/[^/]+/, "")}</div>)}
                  </div>
                )}
              </CardContent></Card>
            );
          })()}

          {d.securityHeaders && (
            <Card><CardContent className="p-4">
              <div className="text-[11px] font-semibold text-muted-foreground mb-2">🔒 Güvenlik Başlıkları</div>
              <div className="space-y-1.5">
                {d.securityHeaders.map((h) => (
                  <div key={h.header} className="text-xs" title={h.why}>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground truncate">{h.header}</span>
                      <b style={{ color: h.ok ? C.green : C.red }}>{h.ok ? "✓" : "eksik"}</b>
                    </div>
                    {h.ok && h.value && <div className="text-[9px] text-muted-foreground truncate">{h.value}</div>}
                  </div>
                ))}
              </div>
              <div className="text-[9px] text-muted-foreground mt-2">Üzerine gel: her başlığın neden gerekli olduğu açıklanır.</div>
            </CardContent></Card>
          )}

          {d.duplicateContent && (() => {
            const dc = d.duplicateContent!;
            return (
              <Card style={dc.flaggedPages > 0 ? { borderColor: `${C.amber}44` } : undefined}><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-2">📄 Kopya İçerik <span className="text-[9px] font-normal">(gerçek metin benzerliği)</span></div>
                <div className="text-2xl font-bold tabular-nums mb-1" style={{ color: dc.flaggedPages ? C.amber : C.green }}>{dc.flaggedPages} <span className="text-xs font-normal text-muted-foreground">bayraklı sayfa</span></div>
                {dc.pairs.length > 0 ? (
                  <div className="max-h-[140px] overflow-y-auto space-y-1.5 text-[10px]">
                    {dc.pairs.map((p, i) => (
                      <div key={i}>
                        <div className="truncate text-muted-foreground" title={p.url1}>{p.url1.replace(/^https?:\/\/[^/]+/, "") || "/"}</div>
                        <div className="truncate">≈ <b style={{ color: p.similarity >= 90 ? C.red : C.amber }}>%{p.similarity}</b> <span title={p.url2}>{p.url2.replace(/^https?:\/\/[^/]+/, "") || "/"}</span></div>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-[10px]" style={{ color: C.green }}>✓ Yüksek benzerlikli sayfa çifti bulunamadı.</div>}
              </CardContent></Card>
            );
          })()}

          {d.microdata && (() => {
            const md = d.microdata!;
            return (
              <Card style={md.pagesWithErrors > 0 ? { borderColor: `${C.amber}44` } : undefined}><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-2">🧬 Schema Doğrulama <span className="text-[9px] px-1.5 py-0.5 rounded ml-1" style={{ background: `${C.amber}20`, color: "#b54708" }}>GEO</span></div>
                <div className="space-y-1 text-xs mb-2">
                  <div className="flex justify-between"><span className="text-muted-foreground">Yapılandırılmış verili sayfa</span><b style={{ color: md.pagesWithSchema ? C.green : C.amber }}>{md.pagesWithSchema}</b></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Schema HATALI sayfa</span><b style={{ color: md.pagesWithErrors ? C.red : C.green }}>{md.pagesWithErrors}</b></div>
                </div>
                {md.errorSamples.length > 0 && (
                  <div className="max-h-[110px] overflow-y-auto space-y-1.5 text-[10px]">
                    {md.errorSamples.map((e, i) => (
                      <div key={i}>
                        <div className="truncate text-muted-foreground" title={e.url}>{e.url.replace(/^https?:\/\/[^/]+/, "") || "/"}</div>
                        {e.messages.map((m, j) => <div key={j} className="truncate text-warning-700 dark:text-warning-400">⚠ {m}</div>)}
                      </div>
                    ))}
                  </div>
                )}
                {md.pagesWithSchema === 0 && <div className="text-[10px] text-warning-700 dark:text-warning-400">⚠ Hiç schema yok — Article/FAQPage/LocalBusiness eklemek zengin sonuç + GEO görünürlüğü kazandırır.</div>}
              </CardContent></Card>
            );
          })()}

          {(d.internalBrokenLinks?.length ?? 0) > 0 && (
            <Card style={{ borderColor: `${C.red}44` }}><CardContent className="p-4">
              <div className="text-[11px] font-semibold text-muted-foreground mb-2">💔 Kırık İç Linkler <span className="text-[9px] font-normal">(kaynak → 404 hedef)</span></div>
              <div className="max-h-[190px] overflow-y-auto space-y-1.5 text-[10px]">
                {d.internalBrokenLinks!.map((l, i) => (
                  <div key={i}>
                    <div className="truncate text-muted-foreground" title={l.sourceUrl}>{l.sourceUrl.replace(/^https?:\/\/[^/]+/, "") || "/"}</div>
                    <div className="truncate">→ <span style={{ color: C.red }}>{l.targetUrl.replace(/^https?:\/\/[^/]+/, "")}</span>{l.anchor ? <span className="text-muted-foreground"> · "{l.anchor.slice(0, 30)}"</span> : null}</div>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
        </div>
        )}

        {/* ── İSTATİSTİK KARTLARI (Semrush Statistics sekmesi) ── */}
        {(d.statusDist || d.depthDist || d.inlinkBuckets || d.canonicalDist || d.markup) && (() => {
          const pct = (v: number, t: number) => (t > 0 ? Math.round((v / t) * 100) : 0);
          const t = d.crawledPages || 1;
          const StatRow = ({ l, v, c }: { l: string; v: number; c?: string }) => (
            <div className="flex items-center justify-between text-xs py-0.5">
              <span className="text-muted-foreground">{l}</span>
              <b className="tabular-nums" style={c ? { color: c } : undefined}>%{pct(v, t)} · {v}</b>
            </div>
          );
          return (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
              {d.statusDist && (
                <Card><CardContent className="p-4">
                  <div className="text-[11px] font-semibold text-muted-foreground mb-2">HTTP Durum Kodu</div>
                  <StatRow l="2xx" v={d.statusDist.c2xx} c={C.green} />
                  <StatRow l="3xx" v={d.statusDist.c3xx} c={C.amber} />
                  <StatRow l="4xx" v={d.statusDist.c4xx} c={C.red} />
                  <StatRow l="5xx" v={d.statusDist.c5xx} c={C.red} />
                </CardContent></Card>
              )}
              {d.depthDist && (
                <Card><CardContent className="p-4">
                  <div className="text-[11px] font-semibold text-muted-foreground mb-2">Tıklama Derinliği</div>
                  <StatRow l="1 tık" v={d.depthDist.d1} c={C.green} />
                  <StatRow l="2 tık" v={d.depthDist.d2} />
                  <StatRow l="3 tık" v={d.depthDist.d3} />
                  <StatRow l="3+ tık" v={d.depthDist.d4plus} c={d.depthDist.d4plus > 0 ? C.amber : undefined} />
                </CardContent></Card>
              )}
              {d.inlinkBuckets && (
                <Card><CardContent className="p-4">
                  <div className="text-[11px] font-semibold text-muted-foreground mb-2">Gelen İç Link</div>
                  <StatRow l="0-1 link" v={d.inlinkBuckets.one} c={d.inlinkBuckets.one > 0 ? C.amber : undefined} />
                  <StatRow l="2-5" v={d.inlinkBuckets.b2_5} />
                  <StatRow l="6-15" v={d.inlinkBuckets.b6_15} />
                  <StatRow l="16-50" v={d.inlinkBuckets.b16_50} />
                  <StatRow l="50+" v={d.inlinkBuckets.b51plus} c={C.green} />
                </CardContent></Card>
              )}
              {d.canonicalDist && (
                <Card><CardContent className="p-4">
                  <div className="text-[11px] font-semibold text-muted-foreground mb-2">Kanonikleştirme</div>
                  <StatRow l="Self-canonical" v={d.canonicalDist.self} c={C.green} />
                  <StatRow l="Başka sayfaya" v={d.canonicalDist.other} c={C.amber} />
                  <StatRow l="Canonical yok" v={d.canonicalDist.none} c={d.canonicalDist.none > 0 ? C.red : undefined} />
                </CardContent></Card>
              )}
              {d.markup && (
                <Card><CardContent className="p-4">
                  <div className="text-[11px] font-semibold text-muted-foreground mb-2">Sosyal Markup</div>
                  <StatRow l="Open Graph" v={d.markup.og} c={C.green} />
                  <StatRow l="Twitter Cards" v={d.markup.twitter} />
                  <StatRow l="İkisi de yok" v={Math.max(0, d.markup.total - Math.max(d.markup.og, d.markup.twitter))} c={C.amber} />
                </CardContent></Card>
              )}
            </div>
          );
        })()}

        {/* ── AI yorumu ── */}
        {(d.aiComment || d.aiCommentError) && (
          <Card className="mb-4" style={{ borderColor: `${C.purple}44` }}><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-2 text-brand-600 dark:text-brand-400">🤖 Teknik SEO Uzmanı Yorumu</div>
            {d.aiComment
              ? <div className="text-sm leading-relaxed whitespace-pre-wrap">{d.aiComment}</div>
              : <div className="text-xs text-warning-700 dark:text-warning-400">⚠️ {d.aiCommentError}</div>}
          </CardContent></Card>
        )}

        {/* ── ISSUE LİSTESİ — her bulgu: neden + çözüm + etkilenen sayfalar ── */}
        <Card id="sorun-listesi" className="mb-4 scroll-mt-4"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="font-semibold text-sm">🔍 Tespit Edilen Sorunlar</span>
            <span className="text-[11px] text-muted-foreground">({shownIssues.length} tür{sevFilter !== "all" ? ` · ${sevMeta[sevFilter].l} filtresi` : ""})</span>
            {sevFilter !== "all" && <button onClick={() => setSevFilter("all")} className="text-[11px] underline text-muted-foreground">tümünü göster</button>}
            <div className="flex items-center gap-1.5 border rounded-lg px-2 py-1.5 ml-auto" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
              <input value={issueSearch} onChange={(e) => setIssueSearch(e.target.value)} placeholder="sorun ara…" className="text-xs bg-transparent outline-none w-36" />
            </div>
          </div>
          {/* Kategori çipleri (Semrush tematik filtreleri) */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            {["all", ...categories].map((c) => {
              const n = c === "all" ? (d?.issues ?? []).length : (d?.issues ?? []).filter((i) => (i.category ?? "Diğer") === c).length;
              return (
                <button key={c} onClick={() => setCatFilter(c)} className="text-[11px] px-2.5 py-1 rounded-full transition"
                  style={catFilter === c ? { background: `${C.purple}20`, color: "#d42b29", fontWeight: 600 } : { background: "rgba(0,0,0,0.05)", color: "#70706c" }}>
                  {c === "all" ? "Tümü" : c} <span className="opacity-60">({n})</span>
                </button>
              );
            })}
          </div>
          <div className="space-y-2">
            {shownIssues.map((iss) => {
              const m = sevMeta[iss.severity];
              const open = openIssue === iss.key;
              return (
                <div key={iss.key} className="rounded-xl border" style={{ borderColor: open ? m.c + "66" : "rgba(0,0,0,0.08)" }}>
                  <button onClick={() => setOpenIssue(open ? null : iss.key)} className="w-full flex items-center gap-3 p-3 text-left">
                    {open ? <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />}
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0" style={{ background: m.bg, color: m.c }}>{m.l}</span>
                    <span className="text-sm font-medium flex-1">
                      {iss.label}
                      {iss.category && <span className="text-[9px] px-1.5 py-0.5 rounded ml-2" style={{ background: "rgba(0,0,0,0.05)", color: "#a3a3a0" }}>{iss.category}</span>}
                      {(iss.newCount ?? 0) > 0 && d.diff && <span className="text-[9px] px-1.5 py-0.5 rounded ml-1.5 font-semibold" style={{ background: `${C.red}15`, color: C.red }}>+{iss.newCount} yeni</span>}
                    </span>
                    <span className="text-sm font-bold tabular-nums shrink-0" style={{ color: m.c }}>{fmt(iss.count)} sayfa</span>
                  </button>
                  {open && (
                    <div className="px-4 pb-4 space-y-2.5">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs">
                        <div className="rounded-lg p-3" style={{ background: "rgba(0,0,0,0.03)" }}>
                          <div className="font-semibold mb-1">Neden önemli?</div>
                          <div className="text-muted-foreground">{iss.why}</div>
                        </div>
                        <div className="rounded-lg p-3" style={{ background: "rgba(34,197,94,0.06)" }}>
                          <div className="font-semibold mb-1 text-success-600 dark:text-success-500">✔ Nasıl çözülür?</div>
                          <div className="text-muted-foreground">{iss.fix}</div>
                        </div>
                      </div>
                      {/* ÇEKİDÜZEN: teknik bilmeyen kullanıcı sorunu web sitecisine TEK TIKLA iletebilsin */}
                      <button
                        onClick={() => {
                          const metin = [
                            `KONU: ${iss.label} (${iss.count} sayfa etkileniyor)`,
                            `ÖNCELİK: ${sevMeta[iss.severity].l}`,
                            ``,
                            `NEDEN ÖNEMLİ: ${iss.why}`,
                            ``,
                            `YAPILMASI GEREKEN: ${iss.fix}`,
                            iss.pages.length ? `\nÖRNEK SAYFALAR:\n${iss.pages.slice(0, 10).map((p) => `- ${p.url}${p.extra ? ` (${p.extra})` : ""}`).join("\n")}` : ``,
                            ``,
                            `— Bu not Geolop teknik denetiminden otomatik oluşturuldu (${new Date().toLocaleDateString("tr-TR")}).`,
                          ].filter(Boolean).join("\n");
                          void navigator.clipboard.writeText(metin).then(
                            () => toast({ title: "📋 Kopyalandı", description: "Sorunun özeti panoya alındı — web sitecine/geliştiricine yapıştırıp gönderebilirsin." }),
                            () => toast({ title: "Kopyalanamadı", description: "Tarayıcı pano erişimine izin vermedi.", variant: "destructive" }),
                          );
                        }}
                        className="rounded-lg border px-3 py-1.5 text-[11.5px] font-medium text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.04]"
                        title="Sorunu, nedenini, çözümünü ve örnek sayfaları tek metin olarak panoya kopyalar."
                      >
                        📋 Web siteciye gönderilecek metni kopyala
                      </button>
                      {iss.pages.length > 0 && (
                        <div className="text-xs">
                          <div className="font-semibold mb-1">Etkilenen sayfalar ({iss.count > iss.pages.length ? `ilk ${iss.pages.length}` : iss.pages.length}):</div>
                          <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                            {iss.pages.map((p) => (
                              <a key={p.url} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 underline decoration-dotted text-muted-foreground hover:text-cyan-600 truncate">
                                {pathOf(p.url)} <ExternalLink className="w-3 h-3 shrink-0 opacity-40" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {shownIssues.length === 0 && <div className="text-sm text-muted-foreground p-4 text-center">🎉 Bu kategoride sorun bulunamadı.</div>}
          </div>
        </CardContent></Card>

        {/* ── EN KÖTÜ SAYFALAR ── */}
        <Card className="mb-4"><CardContent className="p-4">
          <div className="font-semibold text-sm mb-2">📉 En Düşük Skorlu Sayfalar</div>
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs min-w-[860px]">
              <thead className="sticky top-0 bg-white dark:bg-gray-900"><tr className="text-[10px] text-muted-foreground text-left border-b">
                <th className="py-1.5 pr-2">Skor</th><th className="py-1.5">Sayfa</th><th className="py-1.5 text-right">Durum</th><th className="py-1.5 text-right">Yükleme</th><th className="py-1.5 text-right">Boyut</th><th className="py-1.5 text-right">İç/Dış Link</th><th className="py-1.5">Başlıca sorunlar</th>
              </tr></thead>
              <tbody>{d.worstPages.map((p) => (
                <tr key={p.url} className="border-b last:border-0 align-top" style={{ borderColor: "rgba(0,0,0,0.05)" }}>
                  <td className="py-2 pr-2"><span className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded" style={{ background: `${healthColor(p.score)}18`, color: healthColor(p.score) }}>{p.score}</span></td>
                  <td className="py-2 pr-3 max-w-[300px]">
                    <div className="truncate font-medium" title={p.title}>{p.title || "(başlıksız)"}</div>
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[10px] underline decoration-dotted text-muted-foreground truncate block">{pathOf(p.url)}</a>
                  </td>
                  <td className="py-2 text-right tabular-nums" style={{ color: p.statusCode >= 400 ? C.red : p.statusCode >= 300 ? C.amber : undefined }}>{p.statusCode}</td>
                  <td className="py-2 text-right tabular-nums" style={{ color: p.loadTimeMs > 3000 ? C.red : p.loadTimeMs > 1500 ? C.amber : undefined }}>{p.loadTimeMs}ms</td>
                  <td className="py-2 text-right tabular-nums">{fmt(p.sizeKb)}KB</td>
                  <td className="py-2 text-right tabular-nums">{p.internalLinks}/{p.externalLinks}</td>
                  <td className="py-2"><div className="flex flex-wrap gap-1">{p.failing.map((f) => <span key={f} className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "rgba(245,158,11,0.12)", color: "#b54708" }}>{f}</span>)}</div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </CardContent></Card>

        {/* ── Duplicate + Redirect + Non-indexable ── */}
        {(d.duplicateTitles.length > 0 || d.duplicateDescriptions.length > 0 || d.redirectChains.length > 0 || d.nonIndexable.length > 0) && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
            {(d.duplicateTitles.length > 0 || d.duplicateDescriptions.length > 0) && (
              <Card><CardContent className="p-4 text-xs">
                <div className="text-[11px] font-semibold mb-2" style={{ color: C.red }}>👯 Yinelenen Title / Açıklama Grupları</div>
                {[...d.duplicateTitles.map((g) => ({ ...g, t: "Title" })), ...d.duplicateDescriptions.map((g) => ({ ...g, t: "Açıklama" }))].map((g, i) => (
                  <div key={i} className="mb-2.5">
                    <div className="font-medium"><span className="text-[9px] px-1.5 py-0.5 rounded mr-1.5" style={{ background: "rgba(239,68,68,0.1)", color: C.red }}>{g.t}</span>"{g.value.slice(0, 90)}"</div>
                    <div className="text-muted-foreground mt-0.5">{g.pages.map((p) => pathOf(p)).join(" · ")}</div>
                  </div>
                ))}
              </CardContent></Card>
            )}
            {(d.redirectChains.length > 0 || d.nonIndexable.length > 0) && (
              <Card><CardContent className="p-4 text-xs">
                {d.redirectChains.length > 0 && (<>
                  <div className="text-[11px] font-semibold mb-2" style={{ color: C.amber }}>🔁 Yönlendirme Zincirleri</div>
                  {d.redirectChains.map((ch, i) => <div key={i} className="mb-1.5 text-muted-foreground break-all">{ch.map(pathOf).join(" → ")}</div>)}
                </>)}
                {d.nonIndexable.length > 0 && (<>
                  <div className="text-[11px] font-semibold mb-2 mt-3 text-brand-600 dark:text-brand-400">🚫 İndexlenemeyen Sayfalar</div>
                  {d.nonIndexable.map((n) => <div key={n.url} className="mb-1 flex items-center justify-between gap-2"><span className="truncate text-muted-foreground">{pathOf(n.url)}</span><span className="text-[10px] px-1.5 py-0.5 rounded shrink-0" style={{ background: "rgba(139,92,246,0.1)", color: "#d42b29" }}>{n.reason}</span></div>)}
                </>)}
              </CardContent></Card>
            )}
          </div>
        )}
      </>)}
    </div>
  );
}
